import { useState, useEffect } from 'react';

import { faCoins } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InfoIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { useUpdateTokenLimit } from '@/api/mutations/useUpdateTokenLimit';
import { useGetUsersOrganisation } from '@/api/queries/useGetUsersOrganisation';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import { useHasPermission } from '@/hooks/useHasPermission';

export const TokenApprovalLimit = () => {
  const { data: organisation } = useGetUsersOrganisation();
  const updateTokenLimitMutation = useUpdateTokenLimit();

  const [tokenLimit, setTokenLimit] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { hasPermission } = useHasPermission();

  // Initialize values when organisation data is loaded
  useEffect(() => {
    if (organisation?.tokenApprovalLimit !== undefined && organisation.tokenApprovalLimit !== null) {
      setTokenLimit(organisation.tokenApprovalLimit.toString());
    }
  }, [organisation]);

  const handleTokenLimitChange = (value: string) => {
    setTokenLimit(value);
    const originalValue = organisation?.tokenApprovalLimit?.toString() || '';
    setHasUnsavedChanges(value !== originalValue);
  };

  const handleSaveTokenLimit = async () => {
    setIsLoading(true);
    try {
      const tokenApprovalLimit = parseInt(tokenLimit, 10);
      if (isNaN(tokenApprovalLimit) || tokenApprovalLimit < 0) {
        toast.error('Please enter a valid token limit');
        setIsLoading(false);
        return;
      }

      await updateTokenLimitMutation.mutateAsync({ tokenApprovalLimit });

      toast.success('Token limit updated successfully');
      setHasUnsavedChanges(false);
    } catch (error) {
      toast.error('Failed to save token limit');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
          <FontAwesomeIcon icon={faCoins} className="text-blue-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Credit Usage Limits</h2>
          <p className="text-sm text-gray-600">Configure limits at which your users will need to approve uploads.</p>
        </div>
      </div>

      {hasPermission(Permission.TokenLimitsManage) ? (
        <>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="token-limit" className="text-sm font-medium text-gray-700">
                Credit Approval Limit
              </Label>
              <Input
                id="token-limit"
                type="number"
                value={tokenLimit}
                onChange={(e) => handleTokenLimitChange(e.target.value)}
                placeholder="Enter token limit"
                min="0"
                step="1000"
              />
              <p className="text-xs text-gray-500">
                Number of credits which can be uploaded per upload before users will have to explicitly click 'continue'
                before processing will happen.
              </p>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
            <div className="text-sm text-gray-500">
              {hasUnsavedChanges ? (
                <span className="inline-flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                  You have unsaved changes
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500"></div>
                  All changes saved
                </span>
              )}
            </div>

            <Button variant="brand" onClick={handleSaveTokenLimit} disabled={isLoading || !hasUnsavedChanges} size="sm">
              {isLoading ? 'Saving...' : 'Save Limit'}
            </Button>
          </div>
        </>
      ) : (
        <div className="flex gap-2 rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm">
          <InfoIcon className="h-4 w-4 text-gray-500" />
          <p className="text-sm text-gray-500">
            You do not have permission to manage token limits. Please contact your administrator or the Wexler team.
          </p>
        </div>
      )}
    </div>
  );
};
